/**
 * MARKDOWN FOR THE OPERATIONS RUNBOOK, AND NOTHING WIDER.
 *
 * Just enough of Markdown to render the two operational documents in
 * `docs/`, as a pure function from text to blocks. Not a general parser and
 * not trying to be: the whole input set is two files in this repository, and
 * the constructs they use were counted rather than guessed.
 *
 * WHY NOT A LIBRARY. A renderer that produces HTML wants
 * `dangerouslySetInnerHTML` and a sanitizer behind it, which is the shape of
 * every XSS that ever reached a hospital application. This produces data, the
 * component turns it into React elements, and no string is ever interpreted as
 * markup. It also lets a task list, a caution and a table each render as the
 * thing an operator needs to see rather than as generic prose.
 *
 * The content is ours and is read-only, so the risk here is not a hostile
 * author. It is drift: a document gaining a construct this does not know. An
 * unrecognized line becomes a paragraph, which is legible and wrong rather
 * than blank, and `unsupportedConstructs` names anything a test should notice.
 */

export interface Span {
  text: string;
  /** `**bold**` */
  strong?: boolean;
  /** `` `code` `` */
  code?: boolean;
  /** `[text](href)` */
  href?: string;
}

export type Block =
  | { kind: 'heading'; level: number; text: string; id: string }
  | { kind: 'paragraph'; spans: Span[] }
  | { kind: 'code'; text: string }
  | { kind: 'rule' }
  /** `> ...`, which both documents use for a caution rather than a quotation. */
  | { kind: 'callout'; blocks: Block[] }
  | { kind: 'list'; ordered: boolean; items: ListItem[] }
  | { kind: 'table'; head: Span[][]; rows: Span[][][] };

export interface ListItem {
  spans: Span[];
  /** Present when the line was written `- [ ]` or `- [x]`. */
  checked?: boolean;
  /** Indented continuation lines and nested blocks belonging to the item. */
  blocks: Block[];
}

/** A stable anchor for the table of contents. */
export function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[`*_]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

// ============================================================================
// INLINE
// ============================================================================

/**
 * `**bold**`, `` `code` ``, `[text](href)`, and the rest as plain text.
 *
 * Code wins over everything inside it, which is why it is matched first: a
 * command containing an asterisk must not turn half a line bold.
 */
export function spansOf(text: string): Span[] {
  const out: Span[] = [];
  const pattern = /`([^`]+)`|\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let at = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > at) out.push({ text: unescapeEntities(text.slice(at, match.index)) });
    if (match[1] !== undefined) out.push({ text: match[1], code: true });
    else if (match[2] !== undefined) out.push({ text: unescapeEntities(match[2]), strong: true });
    else out.push({ text: unescapeEntities(match[3] ?? ''), href: match[4] ?? '' });
    at = match.index + match[0].length;
  }
  if (at < text.length) out.push({ text: unescapeEntities(text.slice(at)) });
  return out.length > 0 ? out : [{ text: '' }];
}

/** The handful the documents actually write. Not a general entity decoder. */
function unescapeEntities(text: string): string {
  return text
    .replace(/&mdash;/g, '—')
    .replace(/&rsquo;/g, '’')
    .replace(/&ldquo;/g, '“')
    .replace(/&rdquo;/g, '”')
    .replace(/&amp;/g, '&');
}

// ============================================================================
// BLOCKS
// ============================================================================

const HEADING = /^(#{1,6})\s+(.*)$/;
const FENCE = /^\s*```/;
const RULE = /^-{3,}\s*$/;
const TASK = /^\s*-\s+\[([ xX])\]\s+(.*)$/;
const BULLET = /^\s*[-*]\s+(.*)$/;
const ORDERED = /^\s*(\d+)\.\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const TABLE_ROW = /^\s*\|(.*)\|\s*$/;
const TABLE_DIVIDER = /^\s*\|[\s:|-]+\|\s*$/;
/** How far a line must be indented to belong to the list item above it. */
const CONTINUATION = /^ {2,}(\S.*)$/;

export function parseMarkdown(source: string): Block[] {
  return parseLines(source.replace(/\r\n/g, '\n').split('\n'));
}

function parseLines(lines: string[]): Block[] {
  const blocks: Block[] = [];
  // The checker cannot see that `i < lines.length` makes the index safe, and
  // a line past the end reads as empty anyway, which is what ends every loop
  // below. One helper rather than a `?? ''` on each of two dozen reads.
  const at = (n: number): string => lines[n] ?? '';
  let i = 0;

  while (i < lines.length) {
    const line = at(i);

    if (line.trim() === '') { i++; continue; }

    if (RULE.test(line)) { blocks.push({ kind: 'rule' }); i++; continue; }

    const heading = HEADING.exec(line);
    if (heading) {
      const text = (heading[2] ?? '').trim();
      blocks.push({ kind: 'heading', level: (heading[1] ?? '').length, text, id: slug(text) });
      i++;
      continue;
    }

    if (FENCE.test(line)) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !FENCE.test(at(i))) { body.push(at(i)); i++; }
      i++; // the closing fence
      blocks.push({ kind: 'code', text: dedent(body).join('\n') });
      continue;
    }

    if (QUOTE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && QUOTE.test(at(i))) {
        body.push(QUOTE.exec(at(i))?.[1] ?? '');
        i++;
      }
      blocks.push({ kind: 'callout', blocks: parseLines(body) });
      continue;
    }

    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_DIVIDER.test(at(i + 1))) {
      const head = cellsOf(line);
      i += 2;
      const rows: Span[][][] = [];
      while (i < lines.length && TABLE_ROW.test(at(i)) && !TABLE_DIVIDER.test(at(i))) {
        rows.push(cellsOf(at(i)));
        i++;
      }
      blocks.push({ kind: 'table', head, rows });
      continue;
    }

    if (TASK.test(line) || BULLET.test(line) || ORDERED.test(line)) {
      const ordered = !TASK.test(line) && !BULLET.test(line);
      const items: ListItem[] = [];
      while (i < lines.length) {
        const current = at(i);
        const task = TASK.exec(current);
        const bullet = !task ? BULLET.exec(current) : null;
        const numbered = !task && !bullet ? ORDERED.exec(current) : null;
        if (!task && !bullet && !numbered) break;
        // A numbered list and a bulleted one are different lists.
        if ((numbered !== null) !== ordered) break;

        const text = (task ? task[2] : bullet ? bullet[1] : numbered?.[2]) ?? '';
        const item: ListItem = { spans: spansOf(text), blocks: [] };
        if (task) item.checked = (task[1] ?? ' ').toLowerCase() === 'x';
        i++;

        // Everything indented under it, until the next item or a blank run
        // followed by something at the left margin.
        const owned: string[] = [];
        while (i < lines.length) {
          const next = at(i);
          if (next.trim() === '') {
            const after = i + 1 < lines.length ? at(i + 1) : undefined;
            if (after === undefined) { i++; break; }
            if (CONTINUATION.test(after) && !TASK.test(after) && !BULLET.test(after) && !ORDERED.test(after)) {
              owned.push('');
              i++;
              continue;
            }
            i++;
            break;
          }
          if (TASK.test(next) || BULLET.test(next) || ORDERED.test(next)) break;
          const cont = CONTINUATION.exec(next);
          if (!cont) break;
          owned.push(next.replace(/^ {2,6}/, ''));
          i++;
        }
        if (owned.length > 0) item.blocks = parseLines(owned);
        items.push(item);
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }

    // A paragraph runs to the next blank line or the next block construct.
    const body: string[] = [];
    while (i < lines.length && at(i).trim() !== '' && !startsABlock(at(i))) {
      body.push(at(i).trim());
      i++;
    }
    if (body.length === 0) { i++; continue; }
    blocks.push({ kind: 'paragraph', spans: spansOf(body.join(' ')) });
  }

  return blocks;
}

function startsABlock(line: string): boolean {
  return HEADING.test(line) || FENCE.test(line) || RULE.test(line)
    || TASK.test(line) || BULLET.test(line) || ORDERED.test(line)
    || QUOTE.test(line) || TABLE_ROW.test(line);
}

function cellsOf(line: string): Span[][] {
  const inner = TABLE_ROW.exec(line)?.[1] ?? '';
  return inner.split('|').map((cell) => spansOf(cell.trim()));
}

/** Remove the indentation a fenced block shares, keeping its own shape. */
function dedent(lines: string[]): string[] {
  const used = lines.filter((l) => l.trim() !== '');
  if (used.length === 0) return lines;
  const least = Math.min(...used.map((l) => l.length - l.trimStart().length));
  if (!Number.isFinite(least)) return lines;
  return lines.map((l) => l.slice(least));
}

// ============================================================================
// WHAT A DOCUMENT CONTAINS
// ============================================================================

export interface TocEntry { id: string; text: string; level: number }

/** The headings a reader jumps between: the document's own sections. */
export function tableOfContents(blocks: Block[]): TocEntry[] {
  return blocks
    .filter((b): b is Extract<Block, { kind: 'heading' }> => b.kind === 'heading')
    .filter((b) => b.level === 2 || b.level === 3)
    .map((b) => ({ id: b.id, text: b.text, level: b.level }));
}

/**
 * Lines this parser turned into a plain paragraph that look like they meant
 * something else. A test reads this for the real documents, so a runbook that
 * grows a construct nobody taught this about is caught by the suite rather
 * than by an operator finding a mangled page mid-incident.
 */
export function unsupportedConstructs(source: string): string[] {
  const suspicious = [
    { name: 'image', re: /!\[[^\]]*\]\(/ },
    { name: 'html block', re: /^\s*<[a-z]/i },
    { name: 'setext heading', re: /^={3,}\s*$/ },
    { name: 'footnote', re: /^\[\^[^\]]+\]:/ },
    { name: 'definition list', re: /^:\s+\S/ },
  ];
  const found = new Set<string>();
  let inFence = false;
  for (const line of source.split('\n')) {
    if (FENCE.test(line)) { inFence = !inFence; continue; }
    if (inFence) continue;
    for (const s of suspicious) if (s.re.test(line)) found.add(`${s.name}: ${line.trim().slice(0, 60)}`);
  }
  return [...found];
}
